import React, { useState, useRef, useCallback, useContext } from "react";
import { ZegoUIKitPrebuilt } from '@zegocloud/zego-uikit-prebuilt';
import _ from "lodash";
import AuthContext from "context/AuthContext";
import VideoContext from "context/VideoContext";
import DemoContext from "context/DemoContext";
import { useNavigate } from "react-router-dom";
import {collectDeviceInfo} from "components/VideoStream/DeviceInfo";
import { createChatMessage } from 'Services/demoService';
import logger from 'utils/logger';
import { USER_TYPES } from 'utils/constants';
import { useQuestionBanner } from 'hooks/useQuestionBanner';

const isSafariBrowser = () => {
  const ua = navigator.userAgent;
  return /^((?!chrome|android).)*safari/i.test(ua);
};

const patchSafariSetSinkIdGlobally = () => {
  try {
    if (!isSafariBrowser()) return;

    const mediaProto = window.HTMLMediaElement?.prototype;
    if (!mediaProto) return;

    // Prevent double patching
    if (mediaProto.__purebrandSafariPatched) return;
    mediaProto.__purebrandSafariPatched = true;

    // Safari throws: "A user gesture is required"
    if (typeof mediaProto.setSinkId === "function") {
      mediaProto.setSinkId = function () {
        return Promise.resolve();
      };
    }

    const originalPlay = mediaProto.play;
    if (typeof originalPlay === "function") {
      mediaProto.play = function (...args) {
        try {
          this.autoplay = true;
          this.playsInline = true;
        } catch (e) {}

        const result = originalPlay.apply(this, args);
        if (result && typeof result.catch === "function") {
          return result.catch(() => Promise.resolve());
        }
        return result;
      };
    }
  } catch (e) {
    console.error("Safari media patch failed", e);
  }
};

export default function useRoom({setIsLoading}) {
  const { user, loggedIn } = useContext(AuthContext);
  let roomRef = useRef(null);
  let publisherOptionsRef = useRef(null);
  const moderationLeaveRef = useRef(false);
  const [camera, setCamera] = useState(null);
  const [screen, setScreen] = useState(null);
  const [localParticipant, setLocalParticipant] = useState(null);
  const [connected, setConnected] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [networkStatus, setNetworkStatus] = useState(null);
  const [publisherIsSpeaking, setPublisherIsSpeaking] = useState(false);
  const [cameraPublishing, setCameraPublishing] = useState(false);
  const { endCall } = useContext(VideoContext);
  const { setGuestPresence } = useContext(DemoContext);
  const isHost = loggedIn;
  const questionBanner = useQuestionBanner(isHost);

  const navigate = useNavigate();
  const addLocalParticipant = ({ room }) => {
    if (room) {
      setLocalParticipant({
        id: room.participantId,
        name: room.participantName,
      });
    }
  };

  const createCall = useCallback(
    async (
      { appID, serverSecret, roomID, userID, userName, setShowOverlay },
      roomContainer, audioState, videoState
    ) => {
      if (!appID || !roomID || !userID || !userName) {
        throw new Error("Check your credentials");
      }

      const demoInfo = JSON.parse(localStorage.getItem('DETAILS'))?.sessionDetail;

      const fallbackToken =
        demoInfo?.meeting_details?.host_token ||
        demoInfo?.meeting_details?.guest_token ||
        null;
      const zegoToken = localStorage.getItem("ZEGO_TOKEN") || fallbackToken;

      if (!zegoToken) {
        logger.error("ZEGO backend token missing");
      }

      let kitToken;

      try {
        kitToken = ZegoUIKitPrebuilt.generateKitTokenForProduction(
          Number(appID),
          zegoToken,   // raw token from backend (04AAAA...)
          roomID,
          userID,
          userName
        );

        patchSafariSetSinkIdGlobally();

        roomRef.current = ZegoUIKitPrebuilt.create(kitToken);

      } catch (err) {
        logger.error('Zego kit token generation failed', {
          error: err,
        });

        return; 
      }
      const demoIdentifier = demoInfo?.id;
      const guestUserId =
        demoInfo?.members?.guest_detail?.id ||
        demoInfo?.demo_extra_guests?.[0]?.guest?.id;
      try {roomRef.current.joinRoom({
        leaveRoomDialogConfig: {
            confirmCallback: () => {
              if (moderationLeaveRef.current) {
                return;
              }
              setShowOverlay(false);
              setIsLoading(true); // Start loader when API call begins
              if (demoInfo && demoInfo?.status === "UNDECIDED") {
                if (user && loggedIn) {
                    window.location.href = "/user/welcome";
                } else {
                    window.location.href = `/meeting-status-check?demo=${demoIdentifier}&uid=${guestUserId}`;
                }
            } else if (user && loggedIn) {
                // HOST leaving ends the demo for everyone on the call.
                const deviceInfo = collectDeviceInfo();
                const hostUserId = demoInfo?.members?.host_detail?.id;
                const payload = {
                    status: "ENDED", device: "web", user: 'host', device_info: deviceInfo, user_id: hostUserId,
                };
                endCall(demoIdentifier, payload)
                    .then(() => {
                      localStorage.removeItem("ZEGO_TOKEN");
                      localStorage.removeItem("demoId");
                      navigate("/user/welcome");
                    })
                    .catch((err) => {
                      logger.error('Error while ending the host call:: endCall API failed', err);
                      window.location.href = '/user/welcome';
                    })
                    .finally(() => setIsLoading(false));
            } else {
                // GUEST leaving must ONLY mark their own presence -- it must never
                // call endCall/ENDED, which would tear down the demo for the host
                // and every other guest (critical for 1-to-many live demos).
                const deviceInfo = collectDeviceInfo();
                const myGuestId =
                    localStorage.getItem("extra_guest_id") ||
                    demoInfo?.members?.guest_detail?.id ||
                    demoInfo?.demo_extra_guests?.[0]?.guest?.id;
                setGuestPresence(demoIdentifier, {
                    status: false, device: "web", user: 'guest', user_id: myGuestId, device_info: deviceInfo,
                })
                    .then(() => {
                      localStorage.removeItem("ZEGO_TOKEN");
                      navigate(`/meeting-status-check?demo=${demoIdentifier}&uid=${guestUserId}`);
                    })
                    .catch((err) => {
                      logger.error('Error while ending the guest call:: setGuestPresence API call failed', err);
                      navigate(`/meeting-status-check?demo=${demoIdentifier}&uid=${guestUserId}`);
                    })
                    .finally(() => setIsLoading(false));
            }
            }
          },
          showLeavingView:false,
          showroomtimer: true,
          showScreenSharingButton: true,
          autoHideFooter: false,
          screenSharingConfig: {
            resolution: "auto", // You can define custom logic here or set auto resolution
            width: 1280, // Default or auto width, can adjust dynamically
            height: 720,  // Default or auto height, can adjust dynamically
            frameRate: 15, // Set the frame rate, can be set as default or dynamic
            maxBitRate: 1500, // Set the max bit rate, can be set as default or dynamic
            onError: (errorCode) => {
                // Handle errors during screen sharing
                if (errorCode === 1001) {
                    return 'Screen sharing failed due to permission issues';
                } else if (errorCode === 1002) {
                    return 'Screen sharing stopped unexpectedly';
                }
                return ''; // Return an empty string to customize the UI yourself
            }},
          showPreJoinView: false,
          turnOnMicrophoneWhenJoining: !!audioState,
          turnOnCameraWhenJoining: !!videoState,
          container: roomContainer,
          branding:{
            logoURL: "https://purebrand-storage.s3.us-east-2.amazonaws.com/logo1.png"
          },
          scenario: {
              mode: ZegoUIKitPrebuilt.VideoConference,
          },
          onLeaveRoom: () => {
            if (moderationLeaveRef.current) {
              return;
            }
          },
          // onInRoomMessageReceived: (messageInfo) => console.log("Incall message", messageInfo), // Callback for in-room chat message received.
          onInRoomMessageReceived: (messageInfo) => {
            questionBanner.handleChatMessage({
              sender_name: messageInfo.fromUser?.userName ?? 'Participant',
              message:     messageInfo.message ?? '',
              user_id:     messageInfo.fromUser?.userID ?? '',
              urgent:      false,
            });
          },
          onUserJoin: (user) => {
            // When a user joins, add them to the participants
            setParticipants((prev) => [...prev, user]);
          },
          onSendMessageResult: messageInfo => {
            /**
             * Handles the result of sending a chat message.
             * Stores the message in the database silently without showing errors to users.
             *
             * @param {Object} messageInfo
             * @param {number} messageInfo.errCode
             * @param {string} messageInfo.message
             * @param {string} [messageInfo.timestamp]
             * @param {Object} [messageInfo.fromUser]
             * @param {string} [messageInfo.fromUser.userID]
             * @param {number} [messageInfo.sendTime]
             * @param {number} [messageInfo.messageID]
             */
            try {

              // Validate required data
              if (!demoInfo?.id || !messageInfo.message || !messageInfo.fromUser?.userID) {
                return;
              }

              /**
               * Gets the message ID from messageInfo or generates a fallback ID
               *
               * @param {Object} messageInfo - The message info object
               * @return {string} The message ID
               */
              const getMessageId = messageData => {
                if (messageData.messageID) {
                  return messageData.messageID.toString();
                }
                return `msg-${Date.now()}`;
              };

              /**
               * Gets the message time from messageInfo or calculates it from timestamp
               *
               * @param {Object} messageInfo - The message info object
               * @return {number} The message time in milliseconds
               */
              const getMessageTime = message => {
                if (message.sendTime) {
                  return message.sendTime;
                }
                if (message.timestamp) {
                  return parseInt(message.timestamp);
                }
                return Date.now();
              };

              /**
               * Gets the user type based on login status
               *
               * @param {boolean} isLoggedIn - Whether the user is logged in
               * @return {string} The user type (HOST or GUEST)
               */
              const getUserType = isLoggedIn => {
                if (isLoggedIn) {
                  return USER_TYPES.HOST;
                }
                return USER_TYPES.GUEST;
              };

              // Prepare payload for API
              const payload = {
                demo_id: demoInfo?.id,
                message_id: getMessageId(messageInfo),
                message: messageInfo.message,
                message_time: getMessageTime(messageInfo),
                user_id: messageInfo.fromUser.userID,
                user_type: getUserType(loggedIn)
              };

              // Call API to store message (errors are handled silently)
              createChatMessage(payload).catch(error => {
                // Silently handle errors - no user notification
                logger.error('Failed to store chat message in database:', error);
              });
            } catch (error) {
              // Silently handle any unexpected errors
              logger.error('Error processing chat message result:', error);
            }
          },
      });
      } catch (err) {
        logger.error('Zego joinRoom initialization failed', {
          error: err,
        });
        return;
      }
      // AddHostLeaveButton();
      setConnected(true);
      setCamera(roomRef.current.camera);
      setScreen(roomRef.current.screen);
      addLocalParticipant({ room: roomRef.current });

    },
    [setIsLoading]
  );

  // Cleanup function to destroy room refs (only for hosts)
  const destroyRoom = useCallback(() => {
    // Only perform cleanup if user is a host (logged in)
    if (user && loggedIn) {
      if (roomRef?.current) {
        try {
          roomRef?.current?.destroy && roomRef?.current?.destroy();
        } catch {}

        roomRef.current = null;
      }

      if (publisherOptionsRef.current) {
        publisherOptionsRef.current = null;
      }
    }
  }, [user, loggedIn]);

  const markModerationLeave = useCallback(() => {
    moderationLeaveRef.current = true;
  }, []);

  return {
    createCall,
    connected,
    camera,
    screen,
    room: roomRef.current,
    participants,
    networkStatus,
    publisherIsSpeaking,
    cameraPublishing,
    localParticipant,
    destroyRoom,
    markModerationLeave,
    questionBanner,
  };
}
